import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { CheckCircle2, AlertCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { EmbedSnippet } from "../../components/EmbedSnippet";
import { BusinessTaxCard } from "../../components/BusinessTaxCard";

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [optimizing, setOptimizing] = useState(false);
  const [optimizeResult, setOptimizeResult] = useState(null);

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);

  const save = async () => {
    try { const { data } = await api.post("/admin/settings", s); setS(data); toast.success("Settings saved"); }
    catch { toast.error("Failed"); }
  };

  const runOptimize = async (force) => {
    setOptimizing(true); setOptimizeResult(null);
    try {
      const { data } = await api.post(`/admin/maintenance/optimize-covers${force ? "?force=true" : ""}`);
      setOptimizeResult(data);
      toast.success(data.summary || "Done");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Maintenance failed");
    } finally {
      setOptimizing(false);
    }
  };

  if (!s) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div data-testid="admin-settings">
      <div className="border-b border-border pb-4 mb-6">
        <h1 className="font-display text-4xl tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground italic mt-1">Store identity, commerce rules and integrations.</p>
      </div>
      <Tabs defaultValue="store">
        <TabsList className="rounded-sm">
          <TabsTrigger value="store">Store</TabsTrigger>
          <TabsTrigger value="commerce">Commerce</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
          <TabsTrigger value="embed" data-testid="settings-tab-embed">Embed</TabsTrigger>
          <TabsTrigger value="maintenance">Maintenance</TabsTrigger>
        </TabsList>

        <TabsContent value="store" className="mt-6">
          <div className="border border-border rounded-sm p-6 bg-card space-y-4 max-w-xl">
            <div><Label>Store name</Label><Input value={s.store_name || ""} onChange={(e) => setS({ ...s, store_name: e.target.value })} className="rounded-sm mt-1.5" data-testid="set-name" /></div>
            <div><Label>Tagline</Label><Input value={s.store_tagline || ""} onChange={(e) => setS({ ...s, store_tagline: e.target.value })} className="rounded-sm mt-1.5" data-testid="set-tagline" /></div>
          </div>
          <BusinessTaxCard s={s} setS={setS} />
        </TabsContent>

        <TabsContent value="commerce" className="mt-6">
          <div className="border border-border rounded-sm p-6 bg-card space-y-4 max-w-xl">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Min price (AUD)</Label><Input type="number" step="0.01" value={s.min_price} onChange={(e) => setS({ ...s, min_price: parseFloat(e.target.value) })} className="rounded-sm mt-1.5" data-testid="set-min" /></div>
              <div><Label>Max price (AUD)</Label><Input type="number" step="0.01" value={s.max_price} onChange={(e) => setS({ ...s, max_price: parseFloat(e.target.value) })} className="rounded-sm mt-1.5" data-testid="set-max" /></div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="mt-6">
          <div className="border border-border rounded-sm p-6 bg-card space-y-4 max-w-xl">
            <div><Label>Google Analytics 4 Measurement ID</Label>
              <Input value={s.ga_measurement_id || ""} onChange={(e) => setS({ ...s, ga_measurement_id: e.target.value })} placeholder="G-XXXXXXXX" className="rounded-sm mt-1.5 font-mono" data-testid="set-ga" />
              <div className="text-xs text-muted-foreground mt-2 italic">When set, the GA4 tag loads on every public page.</div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="integrations" className="mt-6 space-y-4 max-w-2xl">
          <Integration name="Stripe" mode="LIVE" status="active" detail="Card payments via Stripe Checkout. Secret key stored in backend .env." />
          <Integration name="PayPal" mode="LIVE" status="active" detail="REST Orders v2 integration. Client ID + Secret in backend .env." />
          <Integration name="Resend" mode="LIVE" status="active" detail="Sending from hello@softspinepress.com. Receipts, refunds and newsletters." />
          <Integration name="Object Storage" mode="LIVE" status="active" detail="Emergent Object Storage for PDFs, covers, sample previews and image library." />
          <Integration name="Claude Sonnet 4.5" mode="LIVE" status="active" detail="Powers the support chatbot via the Emergent universal LLM key." />
          <p className="text-xs text-muted-foreground italic">Keys are managed in backend environment variables for security. Email kellie@purplebutterflyweb.com to rotate.</p>
        </TabsContent>

        <TabsContent value="embed" className="mt-6 max-w-2xl">
          <EmbedSnippet />
        </TabsContent>

        <TabsContent value="maintenance" className="mt-6 space-y-6 max-w-2xl">
          <div className="border border-border rounded-sm p-6 bg-card" data-testid="maintenance-covers">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-sm flex items-center justify-center shrink-0 bg-accent/15 text-accent">
                <Sparkles className="h-4 w-4" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <div className="font-medium">Re-canvas all product covers</div>
                <p className="text-xs text-muted-foreground mt-1 italic leading-relaxed">
                  Walks every product cover, fits it onto a clean 1200 × 1800 parchment canvas without cropping,
                  and saves it back as a fast JPEG. Run this once after uploading new artwork or any time you notice
                  thumbnails looking off. It&apos;s safe to run repeatedly — already-optimised covers are skipped.
                </p>
                <div className="flex gap-2 mt-4">
                  <Button
                    onClick={() => runOptimize(false)}
                    disabled={optimizing}
                    className="rounded-sm"
                    data-testid="optimize-covers-btn"
                  >
                    {optimizing ? "Re-canvasing…" : "Re-canvas covers"}
                  </Button>
                  <Button
                    onClick={() => runOptimize(true)}
                    disabled={optimizing}
                    variant="outline"
                    className="rounded-sm"
                    data-testid="optimize-covers-force-btn"
                    title="Re-process every cover even if already at target size"
                  >
                    {optimizing ? "…" : "Force re-canvas"}
                  </Button>
                </div>
                {optimizeResult && (
                  <div className="mt-4 border-t border-border pt-4 text-xs space-y-1" data-testid="optimize-result">
                    <div className="font-medium text-accent">{optimizeResult.summary}</div>
                    {optimizeResult.processed?.length > 0 && (
                      <div className="text-muted-foreground italic mt-2">Updated:</div>
                    )}
                    {(optimizeResult.processed || []).map((p) => (
                      <div key={p.id} className="flex items-baseline justify-between gap-3 text-muted-foreground">
                        <span className="truncate">{p.title}</span>
                        <span className="tabular-nums text-[11px] shrink-0">
                          {p.before_kb}KB → {p.after_kb}KB
                        </span>
                      </div>
                    ))}
                    {(optimizeResult.failed || []).map((f, i) => (
                      <div key={i} className="text-destructive text-[11px]">
                        ✗ {f.id}: {f.error}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <div className="mt-8"><Button onClick={save} className="rounded-sm uppercase tracking-wide" data-testid="save-settings">Save settings</Button></div>
    </div>
  );
}

function Integration({ name, mode, status, detail }) {
  const ok = status === "active";
  return (
    <div className="border border-border rounded-sm p-4 bg-card flex items-start gap-3">
      <div className={`h-9 w-9 rounded-sm flex items-center justify-center shrink-0 ${ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
        {ok ? <CheckCircle2 className="h-4 w-4" strokeWidth={1.5} /> : <AlertCircle className="h-4 w-4" strokeWidth={1.5} />}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <div className="font-medium">{name}</div>
          <Badge variant="outline" className="rounded-sm text-[10px] font-normal">{mode}</Badge>
          <Badge className="rounded-sm text-[10px] font-normal bg-emerald-50 text-emerald-700 border-emerald-200 capitalize">{status}</Badge>
        </div>
        <p className="text-xs text-muted-foreground mt-1 italic">{detail}</p>
      </div>
    </div>
  );
}
